import { bodyById, type CelestialBody } from "./SolarSystem";

/** 2D Keplerian orbital elements derived from state. */
export interface Orbit {
  /** Parent body. */
  bodyId: string;
  /** Semi-major axis, m. Negative for hyperbolic. */
  a: number;
  /** Eccentricity. */
  e: number;
  /** Argument of periapsis, radians. */
  omega: number;
  /** True anomaly at epoch, radians. */
  nu0: number;
  /** Epoch, s. */
  t0: number;
  /** Periapsis, m. */
  periapsis: number;
  /** Apoapsis, m (Infinity for unbound). */
  apoapsis: number;
  /** Period, s (Infinity for unbound). */
  period: number;
}

/** Compute 2D orbital elements from position (rx, ry) and velocity (vx, vy) in body-centered frame. */
export function stateToOrbit(
  bodyId: string,
  rx: number,
  ry: number,
  vx: number,
  vy: number,
  t0: number,
): Orbit {
  const body = bodyById(bodyId);
  const mu = body.mu;
  const r = Math.hypot(rx, ry);
  const v2 = vx * vx + vy * vy;
  const rv = rx * vx + ry * vy;

  // Specific orbital energy
  const eps = v2 / 2 - mu / r;
  const a = -mu / (2 * eps);

  // 2D angular momentum (scalar, z-component)
  const h = rx * vy - ry * vx;

  // Eccentricity vector
  const evx = (v2 / mu - 1 / r) * rx - (rv / mu) * vx;
  const evy = (v2 / mu - 1 / r) * ry - (rv / mu) * vy;
  const e = Math.hypot(evx, evy);

  const omega = Math.atan2(evy, evx);

  // True anomaly
  let nu0: number;
  if (e > 1e-8) {
    const cosNu = (evx * rx + evy * ry) / (e * r);
    const crossZ = evx * ry - evy * rx;
    nu0 = Math.acos(Math.max(-1, Math.min(1, cosNu)));
    if (crossZ < 0) nu0 = -nu0;
  } else {
    nu0 = Math.atan2(ry, rx) - omega;
  }

  const periapsis = a * (1 - e);
  const apoapsis = e >= 1 ? Number.POSITIVE_INFINITY : a * (1 + e);
  const period = e >= 1 ? Number.POSITIVE_INFINITY : 2 * Math.PI * Math.sqrt((a * a * a) / mu);

  return { bodyId, a, e, omega, nu0, t0, periapsis, apoapsis, period };
}

/** Sample the orbit at t seconds after t0 using Kepler's equation (bounded ellipses only). */
export function orbitAt(orbit: Orbit, t: number): { rx: number; ry: number; vx: number; vy: number } {
  const body = bodyById(orbit.bodyId);
  const mu = body.mu;
  const { a, e, omega, nu0, t0, period } = orbit;

  if (e < 1 && period < Number.POSITIVE_INFINITY) {
    // Mean motion
    const n = 2 * Math.PI / period;
    // Convert nu0 -> E0 -> M0
    const E0 = 2 * Math.atan2(Math.sqrt(1 - e) * Math.sin(nu0 / 2), Math.sqrt(1 + e) * Math.cos(nu0 / 2));
    const M0 = E0 - e * Math.sin(E0);
    let M = M0 + n * (t - t0);
    M = ((M % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    // Solve Kepler's eq for E (Newton-Raphson)
    let E = M;
    for (let i = 0; i < 8; i++) {
      const f = E - e * Math.sin(E) - M;
      const fp = 1 - e * Math.cos(E);
      E -= f / fp;
    }
    const nu = 2 * Math.atan2(Math.sqrt(1 + e) * Math.sin(E / 2), Math.sqrt(1 - e) * Math.cos(E / 2));
    const r = a * (1 - e * Math.cos(E));
    const xOrb = r * Math.cos(nu);
    const yOrb = r * Math.sin(nu);
    // Rotate by argument of periapsis
    const cosO = Math.cos(omega), sinO = Math.sin(omega);
    const rx = xOrb * cosO - yOrb * sinO;
    const ry = xOrb * sinO + yOrb * cosO;
    // Velocity in perifocal
    const p = a * (1 - e * e);
    const sqrtMuP = Math.sqrt(mu / p);
    const vxOrb = -sqrtMuP * Math.sin(nu);
    const vyOrb = sqrtMuP * (e + Math.cos(nu));
    const vx = vxOrb * cosO - vyOrb * sinO;
    const vy = vxOrb * sinO + vyOrb * cosO;
    return { rx, ry, vx, vy };
  }

  // Hyperbolic fallback: not needed for kid-friendly gameplay — treat as straight line.
  const dt = t - orbit.t0;
  const r0 = a * (1 - e);
  const rx = r0 * Math.cos(omega) + Math.cos(omega) * (dt * Math.sqrt(mu / Math.abs(a)));
  const ry = r0 * Math.sin(omega) + Math.sin(omega) * (dt * Math.sqrt(mu / Math.abs(a)));
  return { rx, ry, vx: 0, vy: 0 };
}

/** Sample points around the orbit for map rendering. Returns world-space points in body frame. */
export function sampleOrbitPath(orbit: Orbit, nSamples = 64): Array<[number, number]> {
  if (orbit.e >= 1) return [];
  const pts: Array<[number, number]> = [];
  for (let i = 0; i <= nSamples; i++) {
    const frac = i / nSamples;
    const t = orbit.t0 + frac * orbit.period;
    const p = orbitAt(orbit, t);
    pts.push([p.rx, p.ry]);
  }
  return pts;
}

/** Quick orbit classification for HUD text. */
export function classifyOrbit(orbit: Orbit, body: CelestialBody) {
  if (orbit.periapsis < body.radius + body.atmosphereHeight) return "suborbital";
  if (orbit.e >= 1) return "escape";
  return "orbit";
}
